import type { RouteConfig } from "../../config/routes";
import { isScopedAppScope } from "../../utils/scopeRouting";
import type {
  AskVivoRouteContext,
  AskVivoRouteMeta,
  AskVivoRouteScope,
  AskVivoSuggestedQuestion,
} from "./askVivo.types";

export const DEFAULT_ASK_VIVO_QUESTIONS: AskVivoSuggestedQuestion[] = [
  {
    id: "default-1",
    question: "What are the key findings on this page?",
  },
  {
    id: "default-2",
    question: "Are there any notable trends or outliers in this data?",
  },
  {
    id: "default-3",
    question: "What actions would you recommend based on this data?",
  },
];

type AskVivoRouteCandidate = {
  routeKey: string;
  pathPattern: string;
  askVivo: AskVivoRouteMeta;
};

function normalizePathPart(part: string): string {
  return part.replace(/^\/+/, "").replace(/\/+$/, "");
}

function joinRoutePath(parentPath: string, childPath?: string): string {
  if (!childPath) return parentPath;

  const normalizedChildPath = normalizePathPart(childPath);
  if (!parentPath) return normalizedChildPath;

  return `${parentPath}/${normalizedChildPath}`.replace(/\/+/g, "/");
}

function routePatternToKey(pathPattern: string): string {
  return pathPattern.replace(/\/:([^/]+)/g, "/{$1}");
}

function mergeAskVivoMeta(
  inherited: AskVivoRouteMeta | undefined,
  current: AskVivoRouteMeta | undefined,
): AskVivoRouteMeta | undefined {
  if (!inherited && !current) return undefined;

  return {
    ...inherited,
    ...current,
    visibility: current?.visibility ?? inherited?.visibility ?? "hidden",
    screenLabel: current?.screenLabel ?? inherited?.screenLabel,
    scopes: current?.scopes ?? inherited?.scopes,
  };
}

function buildAskVivoRouteCandidates(
  routeConfigs: RouteConfig[],
  parentPath = "",
  inheritedAskVivo: AskVivoRouteMeta | undefined = undefined,
): AskVivoRouteCandidate[] {
  return routeConfigs.flatMap((routeConfig) => {
    const currentPath = routeConfig.index
      ? parentPath
      : joinRoutePath(parentPath, routeConfig.path);
    const resolvedAskVivo = mergeAskVivoMeta(
      inheritedAskVivo,
      routeConfig.askVivo,
    );

    const candidates: AskVivoRouteCandidate[] = [];

    if (resolvedAskVivo && currentPath) {
      candidates.push({
        routeKey: routePatternToKey(currentPath),
        pathPattern: currentPath,
        askVivo: resolvedAskVivo,
      });
      if (routeConfig.wildcardChild) {
        const wildcardPath = `${currentPath}/*`;
        candidates.push({
          routeKey: routePatternToKey(wildcardPath),
          pathPattern: wildcardPath,
          askVivo: resolvedAskVivo,
        });
      }
    }

    if (routeConfig.children?.length) {
      candidates.push(
        ...buildAskVivoRouteCandidates(
          routeConfig.children,
          currentPath,
          resolvedAskVivo,
        ),
      );
    }

    return candidates;
  });
}

function matchesRoutePattern(
  pathPattern: string,
  routeSegment: string,
): boolean {
  const patternParts = pathPattern.split("/").filter(Boolean);
  const segmentParts = routeSegment.split("/").filter(Boolean);

  if (patternParts.at(-1) === "*") {
    const prefixParts = patternParts.slice(0, -1);
    if (segmentParts.length <= prefixParts.length) return false;
    return prefixParts.every((part, index) =>
      part.startsWith(":")
        ? segmentParts[index]?.length > 0
        : part === segmentParts[index],
    );
  }

  if (patternParts.length !== segmentParts.length) {
    return false;
  }

  return patternParts.every((part, index) => {
    if (part.startsWith(":")) {
      return segmentParts[index]?.length > 0;
    }
    return part === segmentParts[index];
  });
}

function resolveVisibility(
  askVivo: AskVivoRouteMeta | undefined,
  scope: AskVivoRouteScope,
): "enabled" | "hidden" {
  if (!askVivo) return "hidden";
  if (askVivo.scopes?.length && !askVivo.scopes.includes(scope)) {
    return "hidden";
  }
  return askVivo.visibility;
}

export function getRouteSegment(
  pathname: string,
  scope: string,
  id: string,
): string {
  if (scope === "admin") {
    const base = "/admin/";
    if (!pathname.startsWith(base)) return "";
    return pathname.slice(base.length);
  }

  if (!isScopedAppScope(scope)) return "";

  if (scope === "portfolio") {
    const base = "/portfolio/";
    if (!pathname.startsWith(base)) return "";
    return pathname.slice(base.length);
  }

  if (!id) return "";
  const base = `/${scope}/${id}/`;
  if (!pathname.startsWith(base)) return "";
  return pathname.slice(base.length);
}

export function resolveAskVivoRouteContext(
  pathname: string,
  scope: string,
  id: string,
  routeConfigs: RouteConfig[],
): AskVivoRouteContext {
  const routeSegment = getRouteSegment(pathname, scope, id);
  const normalizedScope: AskVivoRouteScope =
    scope === "portfolio" ? "portfolio" : scope === "admin" ? "admin" : "trial";

  const candidates = buildAskVivoRouteCandidates(routeConfigs)
    .filter((candidate) =>
      matchesRoutePattern(candidate.pathPattern, routeSegment),
    )
    .sort((left, right) => {
      const leftLen = left.pathPattern.split("/").filter(Boolean).length;
      const rightLen = right.pathPattern.split("/").filter(Boolean).length;
      if (leftLen !== rightLen) return rightLen - leftLen;
      const leftWild = left.pathPattern.endsWith("/*") ? 1 : 0;
      const rightWild = right.pathPattern.endsWith("/*") ? 1 : 0;
      return leftWild - rightWild;
    });

  const matchedCandidate = candidates[0];

  if (!matchedCandidate) {
    return {
      visibility: "hidden",
      screenLabel: "Ask Vivo",
      suggestedQuestions: DEFAULT_ASK_VIVO_QUESTIONS,
      scope: normalizedScope,
      routeSegment,
      freshSession: false,
    };
  }

  const visibility = resolveVisibility(
    matchedCandidate.askVivo,
    normalizedScope,
  );
  const screenLabel = matchedCandidate.askVivo.screenLabel ?? "Ask Vivo";

  return {
    visibility,
    screenLabel,
    suggestedQuestions: DEFAULT_ASK_VIVO_QUESTIONS,
    scope: normalizedScope,
    routeSegment,
    freshSession: matchedCandidate.askVivo.freshSession ?? false,
  };
}
